package com.evoapps.evoshift.ui.util

import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.util.supportedLanguage
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.HelpOutline
import androidx.compose.material3.Icon
import androidx.compose.material3.LocalContentColor
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.platform.testTag
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.util.appLanguage
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.help_centre
import org.jetbrains.compose.resources.stringResource

/**
 * Pages of the help centre built from docs/user/{pl,en} by scripts/docs/build_help_site.py and
 * served at evoshift.app/help (SH-77). The single source for help links: each slug is a page in
 * both languages and each anchor an explicit `{#id}` of a heading on it (HelpLinksTest), so one
 * link works in Polish and English.
 */
enum class HelpPage(val slug: String, val anchor: String? = null) {
    INDEX("index"),
    GETTING_STARTED("getting-started"),
    WORKPLACE_SETTINGS("workplace-settings"),
    WEEKLY_SKELETON("workplace-settings", "weekly-skeleton"),
    TEAM("team-and-invitations"),
    TEAM_PERSON("team-and-invitations", "add-person"),
    /** SH-214: importing the team from a spreadsheet. */
    TEAM_IMPORT("team-and-invitations", "import"),
    ROSTERS("rosters"),
    ROSTER_CREATE("rosters", "create"),
    ROSTER_SKELETON("rosters", "skeleton"),
    ROSTER_BUILD("rosters", "build"),
    /** SH-217: importing a month's roster from a spreadsheet. */
    ROSTER_IMPORT("rosters", "import"),
    CAPACITY("rosters", "capacity"),
    AVAILABILITY("availability"),
    AVAILABILITY_FILL("availability", "fill"),
    AVAILABILITY_WEEK_TEMPLATES("availability", "week-templates"),
    REQUESTS("shift-swaps", "manager-requests"),
    ABSENCES("absences"),
    TODAY_AT_WORK("clocking-in", "today-at-work"),
    /** SH-150: Siri, Google Assistant and the car. */
    VOICE("clocking-in", "voice"),
    DAILY_RESULTS("daily-results-and-finances"),
    DAILY_RECORDS("daily-results-and-finances", "daily-records"),
    FINANCIALS("daily-results-and-finances", "financials"),
    FIXED_COSTS("daily-results-and-finances", "fixed-costs"),
    /** SH-131: the owner's shared costs of the business. */
    SHARED_COSTS("daily-results-and-finances", "shared-costs"),
    LABOUR_COST("labour-cost-and-reports", "labour-cost"),
    NOTIFICATIONS("notifications"),
    NOTIFICATION_SETTINGS("notifications", "push"),
    PLANS("plans-and-billing"),
    EMPLOYEE_GUIDE("employee-guide"),
    MY_SHIFTS("employee-guide", "my-shifts"),
    TRAINING("training"),
    OHS_TRAINING("training", "record"),
    PRIVACY("privacy-and-data"),
    FAQ("faq");

    /** The Markdown file of this page in each language's folder of docs/user. */
    val page: String get() = "$slug.md"

    /** The page in [language] ("pl", "uk" or "en"; anything else gets English), at its section. */
    fun url(language: String = appLanguage()): String {
        val lang = supportedLanguage(language)
        return "$HELP_BASE_URL$lang/$slug.html" + (anchor?.let { "#$it" } ?: "")
    }

    companion object {
        const val HELP_BASE_URL = "https://evoshift.app/help/"
    }
}

/** The test tag of [HelpCentreButton] (robots find it by it). */
const val HELP_CENTRE_TAG = "help_centre"

/**
 * Top-bar "?" that opens [page] of the help centre in the browser, in the app's language. Not to
 * be confused with widgets/HelpButton (SH-64), the small "?" next to a setting that explains it in
 * a dialog.
 */
@Composable
fun HelpCentreButton(page: HelpPage, tint: Color = LocalContentColor.current, modifier: Modifier = Modifier) {
    val uriHandler = LocalUriHandler.current
    val language = appLanguage()
    IconButtonWithTooltip(
        onClick = {
            AppTelemetry.log(AnalyticsEvent.HelpOpened(page.slug))
            uriHandler.openUri(page.url(language))
        },
        modifier = modifier.handCursor().testTag(HELP_CENTRE_TAG),
        tooltip = stringResource(Res.string.help_centre)
    ) {
        Icon(Icons.AutoMirrored.Outlined.HelpOutline, contentDescription = stringResource(Res.string.help_centre), tint = tint)
    }
}
